/*  src/pages/VcOfficeGuests.jsx  */
import React, {
  useEffect, useState, useMemo, useCallback, useContext
} from 'react';
import API                 from '../services/api';
import { AuthContext }     from '../context/AuthContext';
import '../styles/Dashboard.css';

/* ─── helpers ───────────────────────────────────────────── */
const PER_PAGE = 50;
const btn = bg => ({
  background  : bg,
  color       : '#fff',
  border      : 'none',
  borderRadius: 6,
  padding     : '6px 16px',
  cursor      : 'pointer',
  fontSize    : '0.85rem',
});

/* ─── VC-Office guest-registration page ────────────────── */
export default function VcOfficeGuests() {
  const { user } = useContext(AuthContext);
  const userId   = user?._id ?? user?.id ?? '';

  /* master list ------------------------------------------ */
  const [guests, setGuests] = useState([]);
  const fetchGuests = useCallback(
    () => API.get('/guests').then(r => setGuests(r.data)),
    []
  );
  useEffect(() => { fetchGuests(); }, [fetchGuests]);

  /* ────────── search / pagination (current) ────────────── */
  const [search, setSearch] = useState('');
  const [page,   setPage]   = useState(1);

  const mine = useMemo(() => {
    return guests.filter(g => {
      const creator =
        typeof g.gatekeeper === 'string'
          ? g.gatekeeper                       // plain id string
          : g.gatekeeper?._id || g.gatekeeper?.id; // populated object
      return String(creator) === userId;
    });
  }, [guests, userId]);

  const currentFiltered = useMemo(() => {
    const list = mine.filter(g => g.status !== 'out');      // pending / in
    if (!search.trim()) return list;
    const q = search.toLowerCase();
    return list.filter(g =>
      [g.name, g.cnic, g.visitingOffice, g.vehicleNo]
        .some(f => f?.toLowerCase().includes(q)));
  }, [mine, search]);

  const totalPages = Math.max(1, Math.ceil(currentFiltered.length / PER_PAGE));
  const pageRows   = currentFiltered.slice((page-1)*PER_PAGE, page*PER_PAGE);

  /* ────────── previous OUT section ─────────────────────── */
  const [showPrev, setShowPrev] = useState(false);
  const [period,   setPeriod]   = useState('today');   // today | week | month | all
  const [selDate,  setSelDate]  = useState('');
  const [prevPage, setPrevPage] = useState(1);

  /* range helpers */
  const today    = new Date(new Date().setHours(0,0,0,0));
  const tomorrow = new Date(today);  tomorrow.setDate(tomorrow.getDate()+1);
  const weekAgo  = new Date(today);  weekAgo.setDate(weekAgo.getDate()-7);
  const monthAgo = new Date(today);  monthAgo.setMonth(monthAgo.getMonth()-1);

  const prevFiltered = useMemo(() => {
    let list = mine.filter(g => g.status === 'out');

    /* quick range OR explicit date */
    if (selDate) {
      const d0 = new Date(selDate);
      const d1 = new Date(d0); d1.setDate(d1.getDate()+1);
      list = list.filter(g => {
        const d = new Date(g.createdAt);
        return d >= d0 && d < d1;
      });
    } else if (period !== 'all') {
      const keep = d => (
        period==='today' ? d>=today && d<tomorrow :
        period==='week'  ? d>=weekAgo :
        /* month */        d>=monthAgo
      );
      list = list.filter(g => keep(new Date(g.createdAt)));
    }

    /* search string still applies */
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(g =>
        [g.name, g.cnic, g.visitingOffice, g.vehicleNo]
          .some(f => f?.toLowerCase().includes(q)));
    }

    /* newest first */
    return list.sort((a,b) => new Date(b.createdAt) - new Date(a.createdAt));
  }, [mine, period, selDate, search]);

  const prevPages = Math.max(1, Math.ceil(prevFiltered.length / PER_PAGE));
  const prevRows  = prevFiltered.slice((prevPage-1)*PER_PAGE, prevPage*PER_PAGE);

  /* ────────── modal state ──────────────────────────────── */
  const [showModal, setShowModal] = useState(false);
  const [loading,   setLoading]   = useState(false);
  const [form, setForm] = useState({
    name:'', cnic:'', visitingOffice:'',
    vehicleNo:'', visitDate:'', visitTime:'',
  });

  const submit = async e => {
    e.preventDefault();
    if (!/^\d{13}$/.test(form.cnic))
      return alert('CNIC must be exactly 13 digits');

    const { visitDate, visitTime, ...rest } = form;
    const payload = {
      ...rest,
      status   : 'pending',
      createdAt: `${visitDate}T${visitTime}`,
    };

    setLoading(true);
    try {
      await API.post('/guests', payload);
      await fetchGuests();
      setForm({ name:'', cnic:'', visitingOffice:'', vehicleNo:'', visitDate:'', visitTime:'' });
      setShowModal(false);
    } finally { setLoading(false); }
  };

  /* CSV of current+prev (same filtering the user sees) */
  const exportCsv = () => {
    const list = [...currentFiltered, ...prevFiltered];
    if (!list.length) return alert('No guest records to export.');

    const csv = [
      ['Date & Time','Name','CNIC','Office','Vehicle','Status'].join(','),
      ...list.map(g => [
        new Date(g.createdAt).toLocaleString(),
        `"${g.name.replace(/"/g,'""')}"`,
        g.cnic,
        `"${g.visitingOffice.replace(/"/g,'""')}"`,
        g.vehicleNo,
        g.status,
      ].join(',')),
    ].join('\n');

    const blob = new Blob([csv], { type:'text/csv;charset=utf-8' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url; a.download = `guests_${Date.now()}.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  /* ─── UI ─────────────────────────────────────────────── */
  return (
    <div className="dashboard-container">
      {/* ——— header ——— */}
      <div className="dashboard-header">
        <h2>🏛️ VC-Office – Guest Registration</h2>
        <button className="action-btn blue" onClick={() => setShowModal(true)}>
          + New Guest
        </button>
      </div>

      {/* ——— search & export ——— */}
      <div className="search-filter">
        <input
          placeholder="Search guests…"
          value={search}
          onChange={e => { setSearch(e.target.value); setPage(1); setPrevPage(1); }}
        />
        <button className="action-btn green" style={{marginLeft:'auto'}} onClick={exportCsv}>
          ⬇︎ Export CSV
        </button>
      </div>

      {/* ——— CURRENT guests table ——— */}
      <h3 style={{margin:'8px 0'}}>Current Guests (Pending / IN)</h3>
      <div className="table-container prettified">
        <table>
          <thead>
            <tr>
              <th style={{width:150}}>Date &amp; Time</th>
              <th>Name</th><th>CNIC</th><th>Office</th>
              <th>Vehicle</th><th>Status</th>
            </tr>
          </thead>
          <tbody>
            {pageRows.length ? pageRows.map(g => (
              <tr key={g._id}>
                <td>{new Date(g.createdAt).toLocaleString()}</td>
                <td>{g.name}</td>
                <td>{g.cnic}</td>
                <td>{g.visitingOffice}</td>
                <td>{g.vehicleNo}</td>
                <td><span className={`status-badge status-${g.status}`}>{g.status}</span></td>
              </tr>
            )) : (
              <tr><td colSpan={6} className="no-data">No records.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="pagination">
        <button onClick={() => setPage(p => Math.max(1,p-1))} disabled={page===1}>Prev</button>
        <span className="page-info">Page {page} of {totalPages}</span>
        <button onClick={() => setPage(p => Math.min(totalPages,p+1))} disabled={page===totalPages}>Next</button>
      </div>

      {/* ——— toggle previous ——— */}
      <div style={{marginTop:24}}>
        <button className="action-btn yellow" onClick={() => setShowPrev(v => !v)}>
          {showPrev ? 'Hide Previous (OUT)' : 'Show Previous (OUT)'}
        </button>
      </div>

      {/* ——— PREVIOUS guests table ——— */}
      {showPrev && (
        <>
          {/* range buttons */}
          <div className="quick-range" style={{marginTop:12}}>
            {['today','week','month','all'].map(r => (
              <button
                key={r}
                className={period===r?'active period-btn':'period-btn'}
                onClick={() => { setPeriod(r); setSelDate(''); setPrevPage(1); }}
              >
                {{today:'Today',week:'Week',month:'Month',all:'All'}[r]}
              </button>
            ))}
            <input
              type="date"
              value={selDate}
              onChange={e => { setSelDate(e.target.value); setPrevPage(1); }}
              style={{padding:'6px 10px',border:'1px solid #cbd5e1',borderRadius:6}}
            />
          </div>

          <div className="table-container prettified">
            <table>
              <thead>
                <tr>
                  <th style={{width:150}}>Date &amp; Time</th>
                  <th>Name</th><th>CNIC</th><th>Office</th><th>Vehicle</th>
                </tr>
              </thead>
              <tbody>
                {prevRows.length ? prevRows.map(g => (
                  <tr key={g._id}>
                    <td>{new Date(g.createdAt).toLocaleString()}</td>
                    <td>{g.name}</td>
                    <td>{g.cnic}</td>
                    <td>{g.visitingOffice}</td>
                    <td>{g.vehicleNo}</td>
                  </tr>
                )) : (
                  <tr><td colSpan={5} className="no-data">No OUT records.</td></tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="pagination">
            <button onClick={() => setPrevPage(p => Math.max(1,p-1))} disabled={prevPage===1}>Prev</button>
            <span className="page-info">Page {prevPage} of {prevPages}</span>
            <button onClick={() => setPrevPage(p => Math.min(prevPages,p+1))} disabled={prevPage===prevPages}>Next</button>
          </div>
        </>
      )}

      {/* ——— modal ——— */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header"><h3>Register New Guest</h3></div>
            <form className="modal-body" onSubmit={submit}>
              {['name','cnic','visitingOffice','vehicleNo'].map(k => (
                <div className="form-group" key={k}>
                  <label>{{
                    name           :'Guest Name',
                    cnic           :'CNIC (13 digits)',
                    visitingOffice :'Visiting Office',
                    vehicleNo      :'Vehicle No. (optional)',
                  }[k]}</label>
                  <input
                    value={form[k]}
                    onChange={e => setForm({...form,[k]:e.target.value})}
                    {...(k==='cnic' && { pattern:'\\d{13}', title:'13-digit CNIC required' })}
                    {...(k!=='vehicleNo' && { required:true })}
                  />
                </div>
              ))}

              <div className="form-row">
                <div className="form-group" style={{flex:1}}>
                  <label>Date of Visit</label>
                  <input type="date" value={form.visitDate}
                         onChange={e => setForm({...form,visitDate:e.target.value})} required/>
                </div>
                <div className="form-group" style={{flex:1}}>
                  <label>Time</label>
                  <input type="time" value={form.visitTime}
                         onChange={e => setForm({...form,visitTime:e.target.value})} required/>
                </div>
              </div>

              <div className="modal-footer">
                <button type="submit" disabled={loading}>{loading?'Saving…':'Save'}</button>
                <button type="button" className="cancel-btn-form" disabled={loading}
                        onClick={() => setShowModal(false)}>Cancel</button>
              </div>
            </form>
          </div>
          {loading && <div className="loading-box">Saving…</div>}
        </div>
      )}
    </div>
  );
}
